import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsDown, LayoutGrid, ListIcon, Loader2, SearchIcon, XIcon } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import FilePreview, { Extension, PDFPreview } from 'src/components/files/file-previewer'
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from 'src/components/ui/alert-dialog'
import { Pagination } from 'src/components/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/components/ui/select'
import { filterObjectsByValue } from 'src/functions/general/filter'
import { useAppSelector } from 'src/hooks/redux'
import useAuth from 'src/hooks/useAuth'
import { getWindevLang } from 'src/lib/lang'
import { splitArrayIntoChunks } from 'src/lib/pagination'
import { RootState } from 'src/store'
import { useInView } from "react-intersection-observer";
import { queryClient } from 'src/index'
import { useDebounce } from '@uidotdev/usehooks'
import { cn } from 'src/components/lib/utils'
import { Button } from 'src/components/ui/button'

// import axios from 'axios'
import axios from 'src/axios/axios'

const ArticlePreview = ({document}:{document:DocumentObject}) => {
    const { auth } = useAuth()
    const { i18n } = useTranslation()
    const [open, setopen] = useState(false);

    const metatypes:MetatypeObject[] = useAppSelector((state:RootState)=>state.metatypes)

    const [ref, inView] = useInView({
        /* Optional options */
        triggerOnce: true,
        rootMargin: '0px 0px',
    })

    const { data:article, isLoading } = useQuery<DocObject | null>({
        queryKey: [`article-${document.docid}`],
        queryFn: async ({signal}) => {
            try {            
                const { data } = await axios.get(`${process.env.REACT_APP_SERVER_LINK}documents/get?docid=${document.docid}`, { signal })
                if(data.errorcode!==0){ throw new Error(data.message) }
                return data.object
            } catch (err) {
               return null
            }
        }, 
        enabled: inView
    })

    useEffect(() => {
        return () => {
            queryClient.cancelQueries(`article-${document.docid}`)
        };
    }, []);

    function metatypeToValue(metatype:string){
        if(!Array.isArray(metatypes)) return metatype;
        const mt = metatypes.find(i=>i.metatype===metatype)
        if(!mt) return metatype;
        return mt.metatype_oms.find(i=>i.nationid===getWindevLang(i18n.language))?.oms || metatype
    }

    return (
        <AlertDialog open={open} onOpenChange={setopen}>
            <AlertDialogTrigger className='flex items-center justify-center w-full h-full' asChild={article?.filename.includes('pdf')}>
                {
                    (isLoading && !article) ? <Loader2 className='w-5 h-5 text-neutral-400 animate-spin'/> :
                    <>
                    {article?.filename.includes('pdf') 
                    ? 
                    <div className='relative w-full h-full'>
                        <button onClick={()=>{setopen(true)}} className='absolute top-0 left-0 z-10 w-full h-full'></button>
                        
                        <PDFPreview base64={article?.file} options={{ pdf: { showSidebar:false, showZoombar:false, zoom:0.4 } }}/>
                        {/* <iframe ref={ref} src={`data:application/pdf;base64,${article?.file}`} className='object-contain w-full h-full duration-200 border-0 border-none hover:scale-105'/> */}
                    </div>
                    : <img ref={ref} src={article ? `data:image/png;base64,${article?.file}` : '/images/image-placeholder.svg'} className='object-contain w-full h-full duration-200 hover:scale-105'/> 
                    }
                    </>
                }
            </AlertDialogTrigger>
            <AlertDialogContent className='max-w-[90dvw] max-h-[90dvh] overflow-auto'>
                <AlertDialogHeader>
                    <div className='flex items-center justify-between'>
                        <AlertDialogTitle>{document?.metadata?.find(md=>md.metatype==='ARTOMS')?.metavalue}</AlertDialogTitle>
                        <AlertDialogCancel><XIcon className='w-5 h-5'/></AlertDialogCancel>
                    </div>
                    <AlertDialogDescription>{metatypeToValue('ARTNR')}: {document?.metadata?.find(md=>md.metatype==='ARTNR')?.metavalue}</AlertDialogDescription>
                    <AlertDialogDescription>{metatypeToValue('ARTBAR')}: {document?.metadata?.find(md=>md.metatype==='ARTBAR')?.metavalue}</AlertDialogDescription>
                </AlertDialogHeader>
                <div>
                {
                    (isLoading && !article) ? <Loader2 className='w-5 h-5 text-neutral-400 animate-spin'/> :
                    <FilePreview base64={`${article?.file}`} extension={document?.filename.split('.').pop()?.toLowerCase() as Extension}/>
                }
                </div>
            </AlertDialogContent>
        </AlertDialog>
        
    )
}

const PAGE_SIZE = 50

const Home = () => {
    const [ searchParams, setSearchParams ] = useSearchParams()
    const { auth } = useAuth()
    const { i18n } = useTranslation()

    const metatypes:MetatypeObject[] = useAppSelector((state:RootState)=>state.metatypes)

    const [list_view, setlist_view] = useState(true);
    const [page, setpage] = useState(1);
    const [search_filter, setsearch_filter] = useState<string>('');
    const debouncedSearchFilter = useDebounce(search_filter, 500)

    const [allArticles, setAllArticles] = useState<DocumentObject[][]>([[]]);

    useEffect(()=>{
        let URL_PAGE = searchParams.get('page')
        if(URL_PAGE){
            setpage(parseFloat(URL_PAGE))
        }
    }, [])

    useEffect(()=>{
        setSearchParams({['page']: page.toString()})  
        queryClient.cancelQueries(`article-${page-1}`)
        queryClient.cancelQueries(`article-${page+1}`)
    }, [page])

    const fetchArticles = async (page:number, signal:AbortSignal) => {
        try {
            const offset = (page - 1) * PAGE_SIZE;
            const { data } = await axios.get(`${process.env.REACT_APP_SERVER_LINK}documents/getbyfilter?doctypes=ART&offset=${offset}&limit=${PAGE_SIZE}&searchstring=${search_filter}`, {signal});
            if (data.errorcode !== 0) {
                throw new Error(data.message);
            }
            return data.object;
        } catch (error) {
            toast.error('Artikels ophalen mislukt');
            return null;
        }
    };

    const { data:articles, isError, isLoading } = useQuery<DocumentObject[] | null>({
        queryKey: [`articles-${page}-${debouncedSearchFilter}`],
        queryFn: async ({signal}) => {
            const r = await fetchArticles(page, signal)
            if(r){
                setAllArticles(prev => {
                    const updatedArticles = [...prev];
                    updatedArticles[page - 1] = r;
                    return updatedArticles;
                });
                return r
            }
            return null
        },
    })

    function metatypeToValue(metatype:string){
        if(!Array.isArray(metatypes)) return metatype;
        const mt = metatypes.find(i=>i.metatype===metatype)
        if(!mt) return metatype;
        return mt.metatype_oms.find(i=>i.nationid===getWindevLang(i18n.language))?.oms || metatype
    }
    
    //const currentArticles = search_filter ? filterResults([...allArticles].flat()) : allArticles[page - 1] || [];
    const currentArticles = allArticles[page - 1] || [];

  return (
    <div>
        <div className='py-3 bg-primary'>
            <div className='container grid grid-cols-12 gap-4'>
                <div className='flex items-center col-span-3'>
                    <p className='text-2xl font-bold text-white'>{auth.data?.user.companyname}</p>
                </div>
                <div className='flex items-center justify-center col-span-6 bg-white rounded-full'>
                    <input value={search_filter} onChange={({currentTarget})=>{setpage(1); setsearch_filter(currentTarget.value)}} className='w-full h-12 px-4 text-sm rounded-full focus:outline-none' placeholder='Waar ben je naar op zoek?'/>
                    <button className='flex items-center justify-center w-12 h-12 rounded-full text-primary hover:bg-slate-200'>
                        {isLoading ? <Loader2 className='w-5 h-5 animate-spin'/> : <SearchIcon className='w-5 h-5'/>}
                    </button>
                </div>
                <div className='col-span-3'></div>
            </div>
        </div>
        <div className='container max-w-[unset] py-8 '>
            <div className='grid grid-cols-12 gap-4'>
                <main className='col-span-12'>
                    <div className='flex items-center justify-between'>
                        <h1 className='text-3xl font-bold'>Artikelen</h1>
                        <Button onClick={()=>{setlist_view(!list_view)}} variant='ghost'>
                            {list_view ? <ListIcon className='w-4 h-4'/> : <LayoutGrid className='w-4 h-4'/>}
                        </Button>
                    </div>
                    

                    <div className='sticky top-0 z-10 flex items-center justify-between gap-4 py-4 border-b bg-[#F8F9FC]'>
                        <p className='text-sm'>{allArticles.flat()?.length || 0} resultaten ingeladen</p>

                        <div className='flex items-center gap-1'>
                            <button onClick={() => setpage((old) => Math.max(old - 1, 1))} disabled={page === 1 || isLoading} className='flex items-center justify-center w-10 h-10 rounded-md bg-primary/10 hover:bg-primary/20 text-primary disabled:opacity-30'> 
                                {isLoading 
                                ? <Loader2 className='w-5 h-5 animate-spin'/> 
                                : <ChevronLeft className='w-5 h-5'/>
                                }
                            </button>
                            <span className='flex items-center justify-center w-10 h-10'>{page}</span>
                            <button onClick={() => setpage((old) => (!articles || articles.length < PAGE_SIZE ? old : old + 1))} disabled={!articles || articles.length < PAGE_SIZE || isLoading} className='flex items-center justify-center w-10 h-10 rounded-md bg-primary/10 hover:bg-primary/20 text-primary disabled:opacity-30'>
                                {isLoading 
                                ? <Loader2 className='w-5 h-5 animate-spin'/> 
                                : <ChevronRight className='w-5 h-5'/>
                                }    
                            </button>
                        </div>              
                    </div>

                    <div className={cn('mt-8',
                        list_view ? 'flex flex-col gap-2 ' : 'grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-2'
                    )}>
                        {currentArticles && currentArticles.map((article,i)=>(
                            <div className={cn('flex gap-3 bg-white border',
                                list_view ? 'items-center' : 'flex-col'
                            )} key={i}>
                                <div className={cn('flex items-center overflow-hidden bg-white ',
                                    list_view ? 'lg:w-[250px] max-w-[250px] border-r h-40' : 'h-60 justify-center'
                                )}>
                                    <ArticlePreview document={article} />
                                </div>
                                <div className={cn('flex flex-col gap-0.5',
                                    list_view ? '' : 'p-2'
                                )}>
                                    <p className='text-sm text-neutral-500'>{metatypeToValue('ARTNR')}: {article?.metadata?.find(md=>md.metatype==='ARTNR')?.metavalue}</p>
                                    <p className='text-base font-bold'>{article?.metadata?.find(md=>md.metatype==='ARTOMS')?.metavalue}</p>
                                    <p className='text-sm text-neutral-500 line-clamp-5'>{metatypeToValue('ARTBAR')}: {article?.metadata?.find(md=>md.metatype==='ARTBAR')?.metavalue}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                </main>
            </div>
        </div>
    </div>
  )
}

export default Home